/**
 * Custom access profiles — a SUBSET of a base role's permissions (W9-PROFILES-REGISTRIES-KEYS, D475, ADR-0093; DevMobility
 * A-018/A-019; owner decision D461 §5 #11).
 *
 * DevMobility lets an administrator compose a profile freely from screens and actions. Vello does not: a profile is a base
 * role narrowed to an explicit list of words, so it can only ever hold LESS than a role the platform already reviewed —
 * never a combination no role carries. A grant that names a profile authorizes exactly `profile ∩ role`; the role still
 * bounds the scope kinds, and a profile whose words reach admin authority (it can manage operators, profiles, markets or the
 * governed settings) is itself a four-eyes act: a different operator approves it before it can be granted or edited.
 *
 * Pure and country-neutral: no I/O, no clock, no country names.
 */
import { ADMIN_ROLES, permissionGranted, permissionsForRole, roleAllowsScopeType } from './roles.js';
import { DOMAIN_PERMISSIONS, isDomainPermission } from './permissions.js';
/** The words whose holder can change who may do what — a profile carrying one needs a second operator's approval. */
export const ADMIN_AUTHORITY_WORDS = [
    'operator:manage',
    'operator:grant',
    'operator:approve',
    'access_profile:manage',
    'access_profile:approve',
    'market:manage',
    'market:approve',
    'config:publish',
    'config:propose',
    'config:approve',
];
/**
 * The Admin screens a profile may land its holder on after sign-in (the console's route names). A closed list, so a profile can
 * never name a route that does not exist; the Admin pins every entry to a real route in a test.
 */
export const PROFILE_HOME_SCREENS = [
    'dashboard', 'live_map', 'rides', 'dispatch', 'cases', 'safety', 'driver_directory', 'passengers', 'finance', 'campaigns',
    'markets', 'operators', 'governed_settings', 'audit', 'analytics', 'food_ops', 'four_eyes_inbox', 'alert_center', 'notifications',
];
/** The stable reasons a profile write records. */
export const PROFILE_REASON_CODES = ['profile_new_function', 'profile_least_privilege', 'profile_copy', 'profile_correction', 'profile_retire', 'profile_other'];
/** The longest a profile name may be (characters, after trimming). */
export const PROFILE_NAME_MAX = 80;
/** The most words one profile may list (the whole vocabulary is smaller; the bound keeps a payload honest). */
export const PROFILE_WORDS_MAX = 400;
/** The roles a profile may be based on: those a geographic grant can carry (the personas are granted by their own consoles). */
export const PROFILE_BASE_ROLES = ADMIN_ROLES.filter((r) => roleAllowsScopeType(r, 'GLOBAL') || roleAllowsScopeType(r, 'COUNTRY') || roleAllowsScopeType(r, 'CITY'));
/** True when `word` is one the base role grants (wildcards of the role included). */
export function roleHoldsWord(role, word) {
    return permissionGranted(permissionsForRole(role), word);
}
/** True when the profile's words reach admin authority — the profile is then a four-eyes act. */
export function profileCarriesAdminAuthority(permissions) {
    return permissions.some((w) => ADMIN_AUTHORITY_WORDS.includes(w));
}
/**
 * Validate a profile draft. Every word must be an explicit word of the typed vocabulary (no wildcards) AND one the base role
 * grants, so a profile is provably a subset of its role.
 */
export function validateProfile(draft) {
    const issues = [];
    const name = draft.name.trim().replace(/\s+/g, ' ');
    if (name === '' || name.length > PROFILE_NAME_MAX)
        issues.push('NAME_INVALID');
    const baseOk = PROFILE_BASE_ROLES.includes(draft.baseRole);
    if (!baseOk)
        issues.push('BASE_ROLE_INVALID');
    if (draft.permissions.length === 0)
        issues.push('WORDS_REQUIRED');
    if (draft.permissions.length > PROFILE_WORDS_MAX)
        issues.push('WORDS_TOO_MANY');
    const words = [...new Set(draft.permissions.map((w) => w.trim()))].sort();
    if (words.some((w) => !isDomainPermission(w)))
        issues.push('WORD_UNKNOWN');
    else if (baseOk && words.some((w) => !roleHoldsWord(draft.baseRole, w)))
        issues.push('WORD_NOT_IN_BASE_ROLE');
    if (draft.homeScreen !== undefined && !PROFILE_HOME_SCREENS.includes(draft.homeScreen))
        issues.push('HOME_SCREEN_INVALID');
    if (issues.length > 0)
        return { ok: false, issues };
    return {
        ok: true,
        profile: { name, baseRole: draft.baseRole, permissions: words, ...(draft.homeScreen !== undefined ? { homeScreen: draft.homeScreen } : {}) },
    };
}
/**
 * True when defining or changing this profile needs a second operator: its words reach admin authority
 * ({@link ADMIN_AUTHORITY_WORDS}). The base role alone is never the rule — an admin role narrowed to read words is not an
 * admin act; GRANTING a profile based on an admin role is a four-eyes act of its own (the operator console's request).
 */
export function profileNeedsFourEyes(permissions) {
    return profileCarriesAdminAuthority(permissions);
}
/**
 * The words a grant authorizes: its role's words, or — when it names a profile — the profile's words that the role still
 * grants. A word a later change of the role catalog took away is therefore dropped, never kept.
 */
export function effectiveGrantPermissions(grant) {
    if (grant.profile === undefined)
        return permissionsForRole(grant.role);
    return grant.profile.permissions.filter((w) => roleHoldsWord(grant.role, w));
}
/** The explicit words a role offers, for the profile editor's checklist (a role holding `*:*` offers the whole vocabulary). */
export function wordsOfferedByRole(role) {
    const held = permissionsForRole(role);
    return DOMAIN_PERMISSIONS.filter((w) => permissionGranted(held, w));
}
//# sourceMappingURL=access-profile.js.map